import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Scale, LogOut, ChevronRight, Search, RefreshCw,
  Package, TrendingUp, CheckCircle, Phone, Clock, AlertCircle, User, Zap, PhoneCall
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const outcomeColors = {
  pending: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  contacted: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  won: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  lost: "bg-red-500/10 text-red-400 border-red-500/20",
  no_response: "bg-gray-500/10 text-gray-400 border-gray-500/20",
};

const outcomeLabel = {
  pending: "New",
  contacted: "Contacted",
  won: "Won",
  lost: "Lost",
  no_response: "No Response",
};

function getToken() { return localStorage.getItem("attorneyToken"); }
function authHeaders() { return { headers: { Authorization: `Bearer ${getToken()}` } }; }

export default function AttorneyDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterOutcome, setFilterOutcome] = useState("");
  const [filterCall, setFilterCall] = useState("");
  const [error, setError] = useState("");
  const user = JSON.parse(localStorage.getItem("attorneyUser") || "{}");

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes, pkgRes] = await Promise.all([
        axios.get(`${API}/attorney/leads`, authHeaders()),
        axios.get(`${API}/attorney/stats`, authHeaders()),
        axios.get(`${API}/attorney/package`, authHeaders()),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setPkg(pkgRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("attorneyToken");
        navigate("/attorney/login");
      } else {
        setError("Failed to load leads. Please refresh.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = () => {
    localStorage.removeItem("attorneyToken");
    localStorage.removeItem("attorneyUser");
    navigate("/attorney/login");
  };

  const filtered = leads.filter((l) => {
    const matchSearch = !search || [l.name, l.case_category, l.location, l.case_type]
      .some((v) => v?.toLowerCase().includes(search.toLowerCase()));
    const matchOutcome = !filterOutcome || (l.attorney_lead_status || "pending") === filterOutcome;
    const called = !!l.my_call?.called;
    const matchCall = !filterCall || (filterCall === "called" ? called : !called);
    return matchSearch && matchOutcome && matchCall;
  });

  const toggleCallStatus = async (leadId, called, e) => {
    if (e) e.stopPropagation();
    const nextVal = !called;
    setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, my_call: { ...(l.my_call || {}), called: nextVal } } : l)));
    try {
      await axios.put(`${API}/attorney/leads/${leadId}/call-status`, { called: nextVal }, authHeaders());
    } catch {
      load();
    }
  };

  const pkgPct = pkg?.has_package ? Math.round((pkg.leads_used / pkg.leads_limit) * 100) : 0;

  const isPending = user.active === false || user.status === "pending";

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Pending-approval banner */}
      {isPending && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 text-amber-300 text-sm px-4 py-3 text-center">
          Your firm account is <strong>pending approval</strong>. Once our team approves it, you'll start receiving matched leads here.
        </div>
      )}
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center">
              <Scale className="w-3.5 h-3.5 text-primary" />
            </div>
            <span className="font-bold text-sm">Attorney Portal</span>
            <span className="hidden sm:block text-xs text-muted-foreground ml-1">— {user.firm_name || user.name}</span>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition">
            <LogOut className="w-3.5 h-3.5" /> Sign Out
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Leads", value: stats?.total_leads ?? "—", icon: User, color: "text-sky-400" },
            { label: "New / Unread", value: stats?.new_leads ?? "—", icon: Clock, color: "text-amber-400" },
            { label: "Contacted", value: stats?.contacted ?? "—", icon: Phone, color: "text-violet-400" },
            { label: "Won", value: stats?.won ?? "—", icon: CheckCircle, color: "text-emerald-400" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="rounded-xl border border-border bg-card p-4 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg bg-muted flex items-center justify-center ${color}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-2xl font-bold leading-none">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Package bar */}
        {pkg?.has_package ? (
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold capitalize">{pkg.package_type} Package</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{pkg.leads_remaining} leads remaining</span>
                <button onClick={() => navigate("/attorney/subscribe")}
                  className="flex items-center gap-1 text-xs text-primary hover:underline font-medium">
                  <Zap className="w-3 h-3" /> Upgrade
                </button>
              </div>
            </div>
            <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pkgPct >= 80 ? "bg-amber-400" : "bg-primary"}`}
                style={{ width: `${pkgPct}%` }} />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-muted-foreground">{pkg.leads_used} used</span>
              <span className="text-xs text-muted-foreground">{pkg.leads_limit} total</span>
            </div>
            {pkgPct >= 80 && (
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Package almost full
                </p>
                <button onClick={() => navigate("/attorney/subscribe")}
                  className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-3 py-1 rounded-full font-semibold hover:bg-amber-500/20 transition">
                  Renew / Upgrade
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-6 text-center">
            <Package className="w-8 h-8 text-primary mx-auto mb-3" />
            <p className="text-sm font-semibold mb-1">No Active Subscription</p>
            <p className="text-xs text-muted-foreground mb-4">Subscribe to start receiving leads matched to your practice area.</p>
            <button onClick={() => navigate("/attorney/subscribe")}
              className="flex items-center gap-2 mx-auto py-2 px-6 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition">
              <Zap className="w-4 h-4" /> View Plans & Subscribe
            </button>
          </div>
        )}

        {/* Leads table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <h2 className="text-sm font-semibold">Your Leads ({filtered.length})</h2>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-56">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search leads..."
                  className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-background border border-border text-xs focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <select
                value={filterOutcome}
                onChange={(e) => setFilterOutcome(e.target.value)}
                className="py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none"
              >
                <option value="">All Status</option>
                <option value="pending">New</option>
                <option value="contacted">Contacted</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="no_response">No Response</option>
              </select>
              <select
                value={filterCall}
                onChange={(e) => setFilterCall(e.target.value)}
                className="py-1.5 px-2 rounded-lg bg-background border border-border text-xs focus:outline-none"
              >
                <option value="">All Calls</option>
                <option value="called">Called</option>
                <option value="not_called">Not Called</option>
              </select>
              <button onClick={load} className="p-1.5 rounded-lg border border-border hover:bg-muted transition text-muted-foreground">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {error && (
            <div className="p-4 text-sm text-destructive flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          {loading ? (
            <div className="p-12 text-center text-muted-foreground text-sm">Loading your leads...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <TrendingUp className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {leads.length === 0 ? "No leads assigned yet. Leads will appear here once assigned by admin." : "No leads match your filter."}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map((lead) => {
                const outcome = lead.attorney_lead_status || "pending";
                const isNew = !lead.attorney_viewed;
                const isCalled = !!lead.my_call?.called;
                return (
                  <div
                    key={lead._id}
                    onClick={() => navigate(`/attorney/leads/${lead._id}`)}
                    className="flex items-center gap-4 p-4 hover:bg-muted/40 cursor-pointer transition group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium truncate">{lead.name || "Anonymous"}</span>
                        {isNew && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/20 text-primary border border-primary/30">NEW</span>}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{lead.case_category || lead.case_type || "Unknown case"}</span>
                        {lead.location && <span>· {lead.location}</span>}
                        {lead.assignment_date && <span>· {new Date(lead.assignment_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <button
                      onClick={(e) => toggleCallStatus(lead._id, lead.my_call?.called, e)}
                      title={isCalled ? "Mark as Not Called" : "Mark as Called"}
                      className={`flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full border transition flex-shrink-0 ${
                        isCalled
                          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20"
                          : "bg-gray-500/10 text-gray-400 border-gray-500/20 hover:bg-gray-500/20"
                      }`}
                    >
                      <PhoneCall className="w-3 h-3" />
                      {isCalled ? "Called" : "Not Called"}
                    </button>
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${outcomeColors[outcome]}`}>
                      {outcomeLabel[outcome]}
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition flex-shrink-0" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
